import { showAlert } from './showAlert';
import { APP } from '../enums';
import { isWeb } from './platformHelpers';
import { CardPaymentManager } from '../native/CardPaymentManager';
import MapTypes from '../@types/redux/store/MapTypes';
import { getFirstStepsChallenge } from './onBoardingHelper';
import { LocalizePropType } from '../HoC/withTextLocalizer';

export const handleRentalViolations = (
  violations: Record<string, any>,
  user: any,
  localize: LocalizePropType,
  displayNotification: (payload: any) => void,
  devicePaired: boolean,
  navigation: any,
  features: Array<string>,
  sendCode: any,
  helpAction: () => void,
  mIsPaymentVerified: (arg: any) => any,
  mSubmitIdentityShopper: (arg: any) => any,
  mSubmitChallenge: (arg: any) => any,
  mSetRecoverPaymentAuth: (arg: any) => any,
  dispatch: any = null,
  history: any = null
) => {
  const isBlockPaymentNeeded = Object.keys(violations).find(
    (violation) =>
      violation === 'api_error.block_payment_parameter_missing' ||
      violation === 'api_error.block_payment_parameter_missing.paypal'
  );
  if (isBlockPaymentNeeded) {
    // Blocked payment dialog is handled in action reserve car
    return;
  }
  const firstStepsViolations = Object.keys(violations).filter((violationType) =>
    [
      'phoneNumberVerified',
      'paymentMethodExists',
      'validLicence',
      'addressVerified',
      'identityChecked',
    ].includes(violationType)
  );
  const requiredDeviceViolations = Object.keys(violations).filter(
    (violationType) =>
      ['hasPairedDevice', 'isCorrectDevice', 'isPaired'].includes(violationType)
  );
  const paymentVerifyViolation = Object.keys(violations).find(
    (violationType) => violationType === 'api_error.payment.3ds_required'
  );
  const firstStepsChallenge = getFirstStepsChallenge(user.challenges);
  const incompleteTasks = firstStepsChallenge
    ? firstStepsChallenge.required - firstStepsChallenge.achieved
    : 0;

  let action = () => {};

  let licenseStatus: string;
  let IDStatus: string;

  const myProfileAction = () => showMyProfileAction(navigation, history);

  if (requiredDeviceViolations.length > 0) {
    const needAuthorization = user.verified.phoneNumber === false;
    const violationKey = requiredDeviceViolations[0];
    let alertTitle = localize('map.fillProfile.dialog.title');
    let alertMessage = violations[violationKey];
    let yesActionText = localize(
      needAuthorization
        ? 'map.fillProfile.dialog.support'
        : 'map.fillProfile.dialog.add'
    );

    let yesAction = () => {};

    let noActionText = localize('map.fillProfile.dialog.no');

    if (needAuthorization) {
      yesAction = () => {
        showUpdatePhoneViolationAction(navigation, history);
      };
    } else if (!devicePaired) {
      alertTitle = localize('insecuredevice.dialog.title');
      alertMessage = localize('insecuredevice.dialog.message');
      yesActionText = localize('insecuredevice.dialog.yes');

      yesAction = () => {
        showPairingPhoneViolationAction(navigation, history, sendCode);
      };

      noActionText = localize('insecuredevice.dialog.no');
    } else {
      yesAction = () => {
        helpAction();
      };
    }

    showAlert(alertTitle, alertMessage, yesActionText, yesAction, noActionText);
  } else if (firstStepsViolations.length > 1 || incompleteTasks > 1) {
    showAlert(
      localize('map.fillProfile.dialog.title'),
      localize('map.fillProfile.dialog.message'),
      localize('map.fillProfile.dialog.yes'),
      myProfileAction,
      localize('map.fillProfile.dialog.no')
    );
  } else if (firstStepsViolations.length === 1) {
    const violationKey = firstStepsViolations[0];

    switch (violationKey) {
      case 'phoneNumberVerified':
        action = () => {
          showUpdatePhoneViolationAction(navigation, history);
        };
        break;
      case 'paymentMethodExists':
        action = () => {
          showUpdatePaymentViolationAction(navigation, history);
        };
        break;
      case 'validLicence': {
        const { tasks } = user.challenges.find(
          (challenge: { type: string }) => challenge.type === 'firstSteps'
        );
        const { status } = tasks.find(
          (task: { screen: string }) =>
            task.screen === '/user/verify-drivers-license'
        );
        licenseStatus =
          status !== 'completed'
            ? status
            : user.verified.drivingLicense
            ? 'completed'
            : 'deleted';

        action = () => {
          if (user?.remainingLicenseSubmissionAttempts > 0) {
            showUpdateLicenseViolationAction(
              navigation,
              history,
              features,
              licenseStatus
            );
          } else {
            showDLResubmitInvalid(localize, () => {
              helpAction();
            });
          }
        };
        break;
      }
      case 'addressVerified':
        action = () => {
          showUpdateAddressViolationAction(navigation, history);
        };
        break;
      case 'identityChecked': {
        const { tasks } = user.challenges.find(
          (challenge: { type: string }) => challenge.type === 'firstSteps'
        );
        const { status } = tasks.find(
          (task: { screen: string }) =>
            task.screen === '/user/verify-id-document'
        );
        IDStatus =
          status !== 'completed'
            ? status
            : user.verified.identityDocument
            ? 'completed'
            : 'deleted';

        action = () => {
          if (user?.remainingIDSubmissionAttempts > 0) {
            showUpdateIdentityViolationAction(
              navigation,
              history,
              features,
              IDStatus
            );
          } else {
            showIDResubmitInvalid(localize, () => {
              helpAction();
            });
          }
        };
        break;
      }
      default:
        return;
    }

    // @ts-ignore
    if (licenseStatus === 'inProgress') {
      showAlert(
        '',
        localize('user.DL.inProgress.message'),
        null,
        () => {},
        localize('ok'),
        () => {}
      );
    } else {
      showAlert(
        localize('map.fillProfile.dialog.title'),
        violations[violationKey],
        localize('map.fillProfile.dialog.add'),
        () => {
          if (dispatch) {
            dispatch({
              type: MapTypes.SET_SELECTED_CAR,
              payload: { selectedCar: null },
            });
          }
          action();
        },
        localize('map.fillProfile.dialog.no')
      );
    }
  } else if (paymentVerifyViolation) {
    const callback = () => {
      navigation.navigate('MapStack', {
        screen: 'MapCards',
      });
    };

    action = () => {
      mIsPaymentVerified((userData) => {
        if (
          userData?.paymentMethods?.length &&
          userData?.paymentMethods[0]?.paymentData
        ) {
          handle3dsData(
            navigation,
            'BookingViolationsStack',
            userData?.paymentMethods[0]?.paymentData,
            mSubmitIdentityShopper,
            mSubmitChallenge,
            () => mSetRecoverPaymentAuth(true),
            callback,
            callback
          );
        }
      });
    };

    showAlert(
      localize('payment.3ds.title'),
      violations[paymentVerifyViolation],
      localize('continue'),
      action,
      localize('cancel')
    );
  } else {
    if (dispatch) {
      Object.values(violations).forEach((violation) =>
        dispatch(
          displayNotification({
            message: violation,
            type: APP.NOTIFICATION_TYPE.ERROR,
            localize: false,
          })
        )
      );
    } else {
      Object.values(violations).forEach((violation) =>
        displayNotification({
          message: violation,
          type: APP.NOTIFICATION_TYPE.ERROR,
          localize: false,
        })
      );
    }
  }
};

const showMyProfileAction = (navigation: any, history: any) => {
  if (isWeb()) {
    webNavigateTo(history, '/settings');
  } else {
    mobileNavigateTo(navigation, 'DashboardStack', 'Settings');
  }
};

const showUpdatePhoneViolationAction = (navigation: any, history: any) => {
  if (isWeb()) {
    webNavigateTo(history, '/dashboard/addPhone');
  } else {
    mobileNavigateTo(navigation, 'BookingViolationsStack', 'Phone');
  }
};

const showPairingPhoneViolationAction = (
  navigation: any,
  history: any,
  sendCode: any
) => {
  sendCode();
  if (isWeb()) {
    webNavigateTo(history, '/dashboard/verifyPhone');
  } else {
    mobileNavigateTo(
      navigation,
      'BookingViolationsStack',
      'PhonePairingContainer'
    );
  }
};

const showUpdatePaymentViolationAction = (navigation: any, history: any) => {
  if (isWeb()) {
    webNavigateTo(history, '/dashboard/selectPayment');
  } else {
    mobileNavigateTo(navigation, 'BookingViolationsStack', 'SelectPayment');
  }
};

const showUpdateLicenseViolationAction = (
  navigation: any,
  history: any,
  features: Array<string>,
  licenseStatus: string
) => {
  if (['open', 'deleted'].includes(licenseStatus)) {
    if (isWeb()) {
      webNavigateTo(history, '/dashboard/DLOnboarding');
    } else {
      mobileNavigateTo(navigation, 'BookingViolationsStack', 'DL');
    }
  }
};

const showUpdateIdentityViolationAction = (
  navigation: any,
  history: any,
  features: Array<string>,
  IDStatus: string
) => {
  if (['open', 'deleted'].includes(IDStatus)) {
    if (isWeb()) {
      webNavigateTo(history, '/dashboard/IDOnboarding');
    } else {
      mobileNavigateTo(navigation, 'BookingViolationsStack', 'ID');
    }
  }
};

const showUpdateAddressViolationAction = (navigation: any, history: any) => {
  if (isWeb()) {
    webNavigateTo(history, '/dashboard/addAddress');
  } else {
    mobileNavigateTo(navigation, 'BookingViolationsStack', 'Address');
  }
};

const mobileNavigateTo = (
  navigation: any,
  navigationNode: string,
  screen: string
) => {
  navigation.navigate(navigationNode, {
    screen,
  });
};

const webNavigateTo = (history: any, path: string) => {
  history.push(path);
};

export const showDLResubmitInvalid = (
  localize: LocalizePropType,
  positiveCallback: () => void = () => {}
): void => {
  showAlert(
    '',
    localize('user.DL.resubmission.message'),
    localize('user.action.support.text'),
    () => {
      positiveCallback();
    },
    localize('user.notNow')
  );
};

export const showIDResubmitInvalid = (
  localize: LocalizePropType,
  positiveCallback: () => void = () => {}
): void => {
  showAlert(
    '',
    localize('user.ID.resubmission.message'),
    localize('user.action.support.text'),
    () => {
      positiveCallback();
    },
    localize('user.notNow')
  );
};

type SubmitChallengeFn = (data: any, onSuccess: () => void) => void;
type SubmitIdentityShopperFn = (
  data: any,
  onSuccess: () => void,
  onChallenge: (challengeData: any) => void
) => void;

export const handle3dsData = (
  navigation: any,
  stack: string,
  paymentData: any,
  mSubmitIdentityShopper: SubmitIdentityShopperFn,
  mSubmitChallenge: SubmitChallengeFn,
  onRedirectChallengeStarted: () => void,
  successCallback: () => void,
  onPressBack: () => void
) => {
  const { threeDS1, identifyShopperData, challengeShopperData } = paymentData;

  if (threeDS1) {
    onRedirectChallengeStarted();
    handleRedirect(navigation, stack, threeDS1, onPressBack);
  } else if (identifyShopperData) {
    handleIdentifyShopper(
      identifyShopperData,
      mSubmitIdentityShopper,
      mSubmitChallenge,
      successCallback
    );
  } else if (challengeShopperData) {
    handleChallenge(challengeShopperData, mSubmitChallenge, successCallback);
  }
};

export const handleRedirect = (
  navigation: any,
  stack: string,
  data: any,
  onPressBack: () => void
) => {
  navigation.navigate(stack, {
    screen: 'adyen3DSLinkView',
    params: {
      issuerUrl: data?.issuerUrl,
      paRequest: data?.paRequest,
      md: data?.md,
      termUrl: data?.termUrl,
      onPressBack,
    },
  });
};

export const handleChallenge = (
  challengeData: any,
  mSubmitChallenge: SubmitChallengeFn,
  successCallback: () => void
) => {
  const {
    threeDSServerTransID,
    acsTransID,
    acsReferenceNumber,
    acsSignedContent,
  } = challengeData;
  CardPaymentManager.performChallenge(
    threeDSServerTransID,
    acsTransID,
    acsReferenceNumber,
    acsSignedContent,
    (data) => {
      mSubmitChallenge(data, () => {
        successCallback();
      });
    }
  );
};

export const handleIdentifyShopper = (
  identifyShopperData: any,
  mSubmitIdentityShopper: SubmitIdentityShopperFn,
  mSubmitChallenge: SubmitChallengeFn,
  successCallback: () => void
) => {
  const {
    publicKey,
    directoryServerId,
    messageVersion,
    threeDSServerTransID,
    rootCertificates,
  } = identifyShopperData;
  CardPaymentManager.identifyShopper(
    publicKey,
    directoryServerId,
    rootCertificates,
    messageVersion,
    (response) => {
      const sdkEphemeralPublicKeyObj = JSON.parse(
        // @ts-ignore
        response.sdkEphemeralPublicKey
      );
      const dataObject = {
        ...response,
        threeDSServerTransID,
        sdkEphemeralPublicKey: sdkEphemeralPublicKeyObj,
      };
      mSubmitIdentityShopper(
        dataObject,
        () => {
          successCallback();
        },
        (challengeData) => {
          handleChallenge(challengeData, mSubmitChallenge, successCallback);
        }
      );
    }
  );
};
